import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import {
  StoreStatus,
  CallbackDataParams,
  InitializeParams,
} from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { editionService } from "../services/editionService";
import { editionDisplayId } from "./EditionCommonStore";
import { Edition } from "../../../api/graphql";
import { diaDeCalendario } from "../../../common/calendarDate";
import { editionTypeDisplayId } from "../../editionType/store/EditionTypeCommonStore";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import { getEditionDocumentationValues } from "../../editionDocumentation/store/EditionDocumentationCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { getAssociatedDocumentEditionValues } from "../../associatedDocumentEdition/store/AssociatedDocumentEditionCommonStore";
import { getUserAttachmentEditionValues } from "../../userAttachmentEdition/store/UserAttachmentEditionCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { EditionContentType } from "../../../api/graphql";
import { editionTypeService } from "../../editionType/services/editionTypeService";
import { musicalWorkService } from "../../musicalWork/services/musicalWorkService";
import {createAndDownloadCWR} from "./cwr_download";
// import { extractAndMergePdfsFromZip } from "../../../common/utils/extractAndMergePdfsFromZip";
import { FileToDownload } from "../../../common/utils/downloadAllFilesInFolder";
import { tokenManager } from "../../../api/tokenManager";

export class EditionViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = editionService;
  editionInstance: Edition | null = null;
  cardState: Record<string, boolean> = {
    editionData: true,
    contracts: true,
    editionContracts: true,
    editionDocumentation: true,
    editionUserAttachments: true,
    systemDates: false,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  documentationStateValue: string = "";
  documentationLastOperationalErrorValue: string = "";
  publicationDateValue: string | null = null;
  ejemplarPdfFiles: CrudRows = {};
  nameValue: string = "";
  editionTypeService = editionTypeService;
  editionTypeSearchValue: string = "";
  editionTypeIdValue: string = "";
  editionTypeSelectedValue: { displayId: string; id: string } | null = null;
  contentTypeValue: EditionContentType = "" as EditionContentType;
  publicationLinkValue: string = "";
  editionNumberValue: number = 0;
  priceValue: number = 0;
  worksService = musicalWorkService;
  worksSearchValue: string = "";
  worksIdValue: string = "";
  worksSelectedValues: { displayId: string; id: string }[] = [];
  notesValue: string = "";
  dndaExpedienteNumberValue: string = "";
  contractObservationValue: string = "";
  documentationColumnWidths: Record<string, number | string> = {};
  documentationRows: CrudRows = {};
  associatedDocumentsColumnWidths: Record<string, number | string> = {};
  associatedDocumentsRows: CrudRows = {};
  userAttachmentsColumnWidths: Record<string, number | string> = {};
  userAttachmentsRows: CrudRows = {};
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;

  // Estado de firmas electrónicas
  signatureStatuses: Array<{
    editionDocumentationId: string;
    contractNumber: string;
    contractName: string;
    documentStatus: string | null;
    signaturePhase: string | null;
    recipients: Array<{
      name: string;
      email: string;
      role: string;
      order: number | null;
      status: string;
      signedAt: string | null;
    }>;
  }> = [];

  // Estado para el visor de PDF
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  pdfViewerLoading: boolean = false;
  pdfViewerWarning: string = "";

  constructor(
    rootStore: RootStore,
    storeId?: string
  ) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.documentationColumnWidths = {
      finalDocumentPdf: 200,
      documentName: 200,
    };
    this.associatedDocumentsColumnWidths = { file: 200 };
    this.userAttachmentsColumnWidths = { file: 200 };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  get frameCallerData() {
    return null // null?.getCallerData();
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEditionById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore);
    const result = yield api.editionOne({ id });
    this.editionInstance = result.editionOne;
    if (this.editionInstance) {
      this.loadValues(this.editionInstance);
      yield this.loadSignatureStatuses(id);
    }
  }

  *loadSignatureStatuses(editionId: string): any {
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore);
      const result = yield api.editionSignatureStatus({ editionId });
      this.signatureStatuses = result.editionSignatureStatus || [];
    } catch (error) {
      this.signatureStatuses = [];
    }
  }

  *resendSignatureEmail(editionDocumentationId: string, recipientEmail: string): any {
    const api = yield* getApi(this.rootStore.authStore, this.rootStore.activityStore);
    yield api.resendSignatureEmail({ editionDocumentationId, recipientEmail });
    const editionId = this.editionInstance?.id;
    if (editionId) {
      yield this.loadSignatureStatuses(editionId);
    }
  }

  // Recovery: re-verifica las firmas a demanda. Si todas están firmadas, el server
  // congela la SignedEdition (la Edition viva deja de existir). Devuelve el resultado
  // para el feedback del form; si sigue pendiente, refresca el estado de firmas.
  *recheckSignatures(editionId: string): any {
    const api = yield* getApi(this.rootStore.authStore, this.rootStore.activityStore);
    const result = yield api.recheckEditionSignatures({ editionId });
    const checkResult = result.recheckEditionSignatures;
    if (checkResult?.status !== "transitioned") {
      yield this.loadSignatureStatuses(editionId);
    }
    return checkResult;
  }

  get canRecheckSignatures(): boolean {
    return this.rootStore.authStore.hasPermissions(
      "editionDocumentationWorkflow_deleteAndRestartFromSignature"
    );
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.editionInstance) {
      return editionDisplayId(this.editionInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  viewFormWorks(id: string) {
    if (id) {
      this.rootStore.openOrPushOverlay("musicalWork", id, "view");
    }
  }

  *downloadDocumentationPDF(documentId: string): any {
    const documentData = this.documentationRows[`__${documentId}`];

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const pdfFiles = (documentData as any).__finalDocumentPdf_files;

    if (!pdfFiles || pdfFiles.length === 0) {
      console.error(`No hay PDF disponible para el documento: ${documentData.contractNumber}`);
      return;
    }

    const pdfData = pdfFiles[0];

    yield downloadWithAuth({
      entityName: "EditionDocumentation",
      fieldName: "finalDocumentPdf",
      instanceId: documentId,
      fileName: pdfData.name
    });
  }

  *downloadAssociatedDocument(documentId: string): any {
    const documentData = this.associatedDocumentsRows[`__${documentId}`];

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const files = (documentData as any).__file_files;

    if (!files || files.length === 0) {
      console.error(`No hay archivo disponible para: ${documentData.name}`);
      return;
    }

    const fileData = files[0];

    yield downloadWithAuth({
      entityName: "AssociatedDocumentEdition",
      fieldName: "file",
      instanceId: documentId,
      fileName: fileData.name
    });
  }

  openPdfViewer(url: string, title: string, warning?: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerWarning = warning || "";
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
    this.pdfViewerWarning = "";
  }

  *viewDocumentationPDF(documentId: string): any {
    const documentData = this.documentationRows[`__${documentId}`];

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const pdfFiles = (documentData as any).__finalDocumentPdf_files;

    if (!pdfFiles || pdfFiles.length === 0) {
      console.error(`No hay PDF disponible para el documento: ${(documentData as any).contractNumber}`);
      return;
    }

    const pdfData = pdfFiles[0];

    try {
      this.pdfViewerLoading = true;

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
      const serverUrl = apiUrl.replace(/\/graphql$/, "");
      const downloadUrl = `/EditionDocumentation.finalDocumentPdf/${documentId}/${encodeURIComponent(pdfData.name)}`;

      const token = yield tokenManager.getValidAccessToken();
      const response = yield fetch(`${serverUrl}/download${downloadUrl}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = yield response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      this.openPdfViewer(blobUrl, pdfData.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      this.pdfViewerLoading = false;
    }
  }

  // Datos de obras con autores para el gráfico de participación
  get worksWithAuthors(): any[] {
    return this.editionInstance?.works || [];
  }

  get hasDocumentationFiles(): boolean {
    const hasDocumentation = Object.values(this.documentationRows).some(
      (doc: any) => {
        const hasFiles = doc.__finalDocumentPdf_files && doc.__finalDocumentPdf_files.length > 0;
        const hasFileName = doc.finalDocumentPdf && doc.finalDocumentPdf.trim() !== '';
        return hasFiles || hasFileName;
      }
    );

    const hasAssociated = Object.values(this.associatedDocumentsRows).some(
      (doc: any) => {
        const hasFiles = doc.__file_files && doc.__file_files.length > 0;
        const hasFileName = doc.file && doc.file.trim() !== '';
        return hasFiles || hasFileName;
      }
    );

    return hasDocumentation || hasAssociated;
  }

  async downloadAllFiles(): Promise<void> {
    const files: FileToDownload[] = [];

    for (const [key, doc] of Object.entries(this.documentationRows)) {
      const pdfFiles = (doc as any).__finalDocumentPdf_files;
      if (pdfFiles && pdfFiles.length > 0) {
        const documentId = key.replace('__', '');
        files.push({
          entityName: "EditionDocumentation",
          fieldName: "finalDocumentPdf",
          instanceId: documentId,
          fileName: pdfFiles[0].name,
          category: "Contratos"
        });
      }
    }

    for (const [key, doc] of Object.entries(this.associatedDocumentsRows)) {
      const fileList = (doc as any).__file_files;
      if (fileList && fileList.length > 0) {
        const documentId = key.replace('__', '');
        files.push({
          entityName: "AssociatedDocumentEdition",
          fieldName: "file",
          instanceId: documentId,
          fileName: fileList[0].name,
          category: "Boletines"
        });
      }
    }

    const editionName = this.nameValue || "Edicion";

    const result = await this.rootStore.fileDownloadService.download({
      downloadName: editionName,
      files
    });

    if (result.failed.length > 0) {
      console.error("Archivos fallidos:", result.failed);
      alert(
        `Descarga completada con errores:\n` +
        `Exitosos: ${result.successful}/${result.total}\n` +
        `Fallidos: ${result.failed.length}\n\n` +
        result.failed.map(f => `- ${f.fileName} (${f.category}): ${f.error}`).join('\n')
      );
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "editionType") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore);
        const result = yield api.editionTypeOne({
          id: params.returnData.resultId,
        });
        const editionType = result.editionTypeOne;
        this.editionTypeSelectedValue = editionType;
      } else if (params.callerData?.fieldName === "works") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore);
        const result = yield api.musicalWorkOne({
          id: params.returnData.resultId,
        });
        const musicalWork = result.musicalWorkOne;
        this.worksSelectedValues.push(musicalWork);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get editionTypeValue() {
    if (this.editionTypeSelectedValue) {
      return {
        id: this.editionTypeSelectedValue.id,
        displayId: editionTypeDisplayId(
          this.editionTypeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  getContentTypeOptions() {
    return this.langStore.EditionContentTypeOptions;
  }

  get worksValues() {
    return this.worksSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: musicalWorkDisplayId(x, this.langStore),
      };
    });
  }

  generateCWR() {
    if (this.editionInstance !== null) {
      try {
        createAndDownloadCWR(this.editionInstance);
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          let message = apiError.message;
          this.showErrorDialog(error, apiError.title, message);
        } else {
          this.showErrorDialog(error, this.langStore.serverError, undefined);
        }
      }
    }
  }

  get documentationVisibleColumns() {
    return [
      {
        name: "finalDocumentPdf",
        title: this.langStore.EditionDocumentationFinalDocumentPdfLabel,
        width: this.documentationColumnWidths["finalDocumentPdf"],
      },
      {
        name: "documentName",
        title: this.langStore.EditionDocumentationDocumentNameLabel,
        width: this.documentationColumnWidths["documentName"],
      },
    ];
  }

  documentationSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.documentationColumnWidths = values;
  }

  get documentationRowsDyn() {
    return this.documentationRows;
  }

  get associatedDocumentsVisibleColumns() {
    return [
      {
        name: "file",
        title: this.langStore.AssociatedDocumentEditionFileLabel,
        width: this.associatedDocumentsColumnWidths["file"],
      },
    ];
  }

  associatedDocumentsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.associatedDocumentsColumnWidths = values;
  }

  get associatedDocumentsRowsDyn() {
    return this.associatedDocumentsRows;
  }

  get userAttachmentsVisibleColumns() {
    return [
      {
        name: "file",
        title: this.langStore.UserAttachmentEditionFileLabel,
        width: this.userAttachmentsColumnWidths["file"],
      },
    ];
  }

  userAttachmentsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.userAttachmentsColumnWidths = values;
  }

  get userAttachmentsRowsDyn() {
    return this.userAttachmentsRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "Edition",
      fieldName: fieldName,
      instanceId: this.editionInstance?.id!,
      fileName: props.name,
    });
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "Edition",
        fieldName: fieldName,
        instanceId: this.editionInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.editionInstance = null;
    this.documentationStateValue = "";
    this.documentationLastOperationalErrorValue = "";
    this.publicationDateValue = null;
    this.ejemplarPdfFiles = {};
    this.nameValue = "";
    this.editionTypeSearchValue = "";
    this.editionTypeIdValue = "";
    this.contentTypeValue = "" as EditionContentType;
    this.publicationLinkValue = "";
    this.editionNumberValue = 0;
    this.priceValue = 0;
    this.worksSearchValue = "";
    this.worksIdValue = "";
    this.worksSelectedValues = [];
    this.notesValue = "";
    this.dndaExpedienteNumberValue = "";
    this.documentationStateValue = "";
    this.signatureStatuses = [];
    this.contractObservationValue = "";
    this.documentationRows = {};
    this.associatedDocumentsRows = {};
    this.userAttachmentsRows = {};
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEdition() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("edition_update");
  }

  editEdition() {
    const id = this.editionInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/edition/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEdition(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEdition(): any {
    yield this.close();
  }

  loadValues(instance: Edition) {
    this.idValue = instance.id;
    this.documentationStateValue = instance.documentationState?.id || "";
    this.documentationLastOperationalErrorValue =
      instance.documentationLastOperationalError || "";
    this.publicationDateValue = diaDeCalendario(instance.publicationDate);
    this.ejemplarPdfFiles = instance.ejemplarPdf_data
      ? {
          [instance.ejemplarPdf_data.name]: {
            name: instance.ejemplarPdf_data.name,
            formattedSize: formatBytes(instance.ejemplarPdf_data?.size || 0),
            mimetype: instance.ejemplarPdf_data?.mimetype,
            id: instance.ejemplarPdf_data.name,
            __status__: "original",
          },
        }
      : {};
    this.nameValue = instance.name;
    this.editionTypeSelectedValue = instance.editionType;
    this.contentTypeValue = instance.contentType;
    this.publicationLinkValue = instance.publicationLink || "";
    this.editionNumberValue = instance.editionNumber;
    this.priceValue = instance.price;
    this.worksSelectedValues = instance.works ? instance.works : [];
    this.notesValue = instance.notes || "";
    this.dndaExpedienteNumberValue = instance.dndaExpedienteNumber || "";
    this.contractObservationValue = instance.contractObservation || "";
    this.documentationRows = instance.documentation
      ? Object.assign(
          {},
          ...instance.documentation.map((x) => {
            const processedRow = getEditionDocumentationValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.associatedDocumentsRows = instance.associatedDocuments
      ? Object.assign(
          {},
          ...instance.associatedDocuments.map((x) => {
            const processedRow = getAssociatedDocumentEditionValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.userAttachmentsRows = instance.userAttachments
      ? Object.assign(
          {},
          ...instance.userAttachments.map((x) => {
            const processedRow = getUserAttachmentEditionValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEditionById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
